import "./CompanyDividendsTabComponent.scss";
import template from "./CompanyDividendsTabComponent.html";
import {IComponentOptions} from "angular";
import {DividendsStore} from "../../../dividends/store/DividendsStore";
import {CodeAndExchange, securityEquality} from "../../../instruments/types/CodeAndExchange";
import {Observable, ReplaySubject} from "rxjs";
import {DividendsModel} from "../../../dividends/models/DividendsModel";
import {Dividend, orderDividendsByDateDesc} from "../../../dividends/types/DividendsRecord";
import {Optional} from "../../../js/util/Optional";
import {isNotNull} from "../../../js/util/UtilFunctions";
import moment from "moment";
import {formatDate} from "../../../js/util/DateTimeUtil";

export const CompanyDividends = "tfCompanyDividendsTab";

export function companyDividends(): IComponentOptions {
    return {
        template,
        bindings: {
            security: "<"
        },
        controllerAs: "ctrl",
        controller: CompanyDividendsTabComponentController
    };
}

export class CompanyDividendsTabComponentController {

    /** @input */
    public set security(security: CodeAndExchange | null) {
        if (security != null) {
            this.security$.next(security);
        }
    }

    private readonly security$ = new ReplaySubject<CodeAndExchange>(1);

    public readonly dividends$: Observable<DividendsModel | null>;
    public readonly hasDividends$: Observable<boolean>;
    public readonly nextDividend$: Observable<Dividend | null>;
    public readonly hasNextDividend$: Observable<boolean>;

    /** @ngInject */
    public constructor(private readonly dividendsStore: DividendsStore) {
        this.dividends$ = this.buildDividends();
        this.hasDividends$ = this.buildHasDividends();
        this.nextDividend$ = this.buildNextDividend();
        this.hasNextDividend$ = this.buildHasNextDividend();
    }

    private buildDividends(): Observable<DividendsModel | null> {
        return this.security$
                   .distinctUntilChanged(securityEquality())
                   .switchMap((security) => this.dividendsStore.stream(security))
                   .map((model) => this.lastFourYears(model))
                   .publishReplay(1)
                   .refCount();
    }

    private lastFourYears(model: DividendsModel): DividendsModel {
        if (model.isEmpty()) {
            return model;
        }
        const latest = [...model.dividends].sort(orderDividendsByDateDesc())
                                           .map((d) => d.exDate)
                                           .map((d) => moment(d))[0]!;
        const cutoff = latest.clone().subtract(3, "years").format("YYYY");
        return model.filter((d) => formatDate(d.exDate, "YYYY") >= cutoff);
    }

    private buildHasDividends(): Observable<boolean> {
        return this.dividends$.map(isNotNull);
    }

    private buildNextDividend(): Observable<Dividend | null> {
        return this.dividends$
                   .map((model) => Optional.of(model).map((m) => m.next()).orNull())
                   .publishReplay(1)
                   .refCount();
    }

    private buildHasNextDividend(): Observable<boolean> {
        return this.nextDividend$.map(isNotNull);
    }
}
